// Upload Wizard Progress Service - Persists upload wizard progress/status
import { UploadProgress } from '@types';

interface UploadWizardProgressState {
  fileId: string;
  fileName: string;
  progress: UploadProgress;
  uploadedAt: number;
}

class UploadWizardProgressService {
  private storageKey = 'fyle_upload_wizard_progress';
  private maxStorageAge = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Save or update upload progress for a file
   */
  saveProgress(progress: UploadProgress): void {
    const allProgress = this.getAllProgress();
    
    // Remove old entry if exists
    const filtered = allProgress.filter((p) => p.fileId !== progress.fileId);
    
    // Add/update progress
    filtered.push({
      fileId: progress.fileId,
      fileName: progress.fileName,
      progress,
      uploadedAt: Date.now(),
    });
    
    // Save to localStorage
    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
  }

  /**
   * Get progress for a specific file
   */
  getProgress(fileId: string): UploadProgress | null {
    const allProgress = this.getAllProgress();
    const state = allProgress.find((p) => p.fileId === fileId);
    
    if (!state) return null;
    
    // Check if expired
    const age = Date.now() - state.uploadedAt;
    if (age > this.maxStorageAge) {
      this.removeProgress(fileId);
      return null;
    }
    
    return state.progress;
  }

  /**
   * Get all progress entries
   */
  getAllProgressEntries(): UploadProgress[] {
    const allProgress = this.getAllProgress();
    const now = Date.now();
    
    return allProgress
      .filter((p) => now - p.uploadedAt < this.maxStorageAge)
      .map((p) => p.progress);
  }

  /**
   * Get all incomplete uploads
   */
  getIncompleteUploads(): UploadProgress[] {
    const allProgress = this.getAllProgressEntries();
    return allProgress.filter(
      (p) => p.status !== 'completed' && p.status !== 'error'
    );
  }

  /**
   * Remove progress for a specific file
   */
  removeProgress(fileId: string): void {
    const allProgress = this.getAllProgress();
    const filtered = allProgress.filter((p) => p.fileId !== fileId);
    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
  }

  /**
   * Remove all progress
   */
  clearAll(): void {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Clean expired progress entries
   */
  cleanExpired(): void {
    const allProgress = this.getAllProgress();
    const now = Date.now();
    const filtered = allProgress.filter(
      (p) => now - p.uploadedAt < this.maxStorageAge
    );
    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
  }

  /**
   * Get all progress from localStorage
   */
  private getAllProgress(): UploadWizardProgressState[] {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to get upload wizard progress:', error);
      return [];
    }
  }
}

// Export singleton instance
export const uploadWizardProgressService = new UploadWizardProgressService();

// Clean expired entries on load
if (typeof window !== 'undefined') {
  uploadWizardProgressService.cleanExpired();
  
  // Also clean every hour
  setInterval(() => {
    uploadWizardProgressService.cleanExpired();
  }, 60 * 60 * 1000);
}

